import React from 'react';
import type { ReportTheme } from '../../../../types/reportTheme';
import { roleColor } from '../../../../lib/reportRoleColors';
import { reportShellBodyFontSize, REPORT_TEXT_PX } from '../../../../lib/reportSectionHeading';

const MONO = 'var(--report-body-font, inherit)';

export interface EditorialDefinitionItem {
  label: React.ReactNode;
  value: React.ReactNode;
}

export interface EditorialDefinitionListProps {
  theme: ReportTheme;
  items: EditorialDefinitionItem[];
  id?: string;
  className?: string;
  style?: React.CSSProperties;
  /**
   * William QA 2026-06-29 — when true, drop the per-item hairline TOP rule. Used
   * by the Conclusion verdict block (Top Strengths / Top Risks) where the rule
   * sat directly above the "TOP STRENGTHS" label as an unwanted line.
   */
  hideItemRule?: boolean;
}

function resolveScale(
  scale: ReportTheme['headingScale'] | undefined,
): ReportTheme['headingScale'] {
  return scale === 'editorial' || scale === 'technical' || scale === 'compact'
    ? scale
    : 'editorial';
}

export function EditorialDefinitionList({
  theme,
  items,
  id,
  className,
  style,
  hideItemRule = false,
}: EditorialDefinitionListProps): React.ReactElement {
  const scale = resolveScale(theme.headingScale);
  const dense = theme.density === 'compact';

  let listStyle: React.CSSProperties;
  let termStyle: React.CSSProperties;
  let descStyle: React.CSSProperties;
  let itemStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    ...(hideItemRule ? {} : { borderTop: `1px solid ${roleColor(theme, 'hair')}` }),
  };

  if (scale === 'technical') {
    listStyle = { display: 'flex', flexDirection: 'column', gap: dense ? 6 : 8 };
    itemStyle = { ...itemStyle, paddingTop: dense ? 4 : 6, paddingBottom: dense ? 4 : 6 };
    termStyle = {
      fontFamily: MONO,
      // 12px label/caption token (text-xs) — unified across editorial terms.
      fontSize: REPORT_TEXT_PX.label,
      fontWeight: 600,
      letterSpacing: '0.12em',
      textTransform: 'uppercase',
      color: roleColor(theme, 'muted'),
      marginBottom: dense ? 2 : 3,
    };
    descStyle = {
      color: roleColor(theme, 'body'),
      // 14px unified report BODY size (text-sm).
      fontSize: reportShellBodyFontSize(theme),
      lineHeight: 1.5,
    };
  } else if (scale === 'compact') {
    listStyle = { display: 'flex', flexDirection: 'column', gap: dense ? 4 : 6 };
    itemStyle = { ...itemStyle, paddingTop: dense ? 3 : 4, paddingBottom: dense ? 3 : 4 };
    termStyle = {
      fontFamily: MONO,
      // 12px label/caption token (text-xs) — unified across editorial terms.
      fontSize: REPORT_TEXT_PX.label,
      fontWeight: 600,
      letterSpacing: '0.1em',
      textTransform: 'uppercase',
      color: roleColor(theme, 'muted'),
      marginBottom: dense ? 1 : 2,
    };
    descStyle = {
      color: roleColor(theme, 'body'),
      // 14px unified report BODY size (text-sm).
      fontSize: reportShellBodyFontSize(theme),
      lineHeight: 1.4,
    };
  } else {
    // editorial
    listStyle = { display: 'flex', flexDirection: 'column', gap: dense ? 8 : 12 };
    itemStyle = { ...itemStyle, paddingTop: dense ? 6 : 8, paddingBottom: dense ? 6 : 8 };
    termStyle = {
      fontFamily: MONO,
      // 12px label/caption token (text-xs) — unified across editorial terms.
      fontSize: REPORT_TEXT_PX.label,
      fontWeight: 600,
      letterSpacing: '0.14em',
      textTransform: 'uppercase',
      color: roleColor(theme, 'muted'),
      marginBottom: dense ? 3 : 4,
    };
    descStyle = {
      color: roleColor(theme, 'body'),
      // 14px unified report BODY size (text-sm).
      fontSize: reportShellBodyFontSize(theme),
      lineHeight: 1.6,
    };
  }

  return (
    <dl id={id} className={className} style={{ ...listStyle, ...style, margin: 0 }}>
      {items.map((item, i) => (
        // Paged.js ignores break-after:avoid on <dt>. The shared head-body
        // contract instead puts break-before:avoid on the following <dd>, so
        // a term can never be left at the foot of a page while the opening of
        // its definition moves overleaf. The group remains fragmentable: long
        // definitions can still continue after their opening lines.
        <div key={i} style={itemStyle} data-layout-group="head-body">
          <dt style={termStyle}>{item.label}</dt>
          <dd style={{ ...descStyle, margin: 0 }}>{item.value}</dd>
        </div>
      ))}
    </dl>
  );
}
